import { EventEmitter, Inject, inject, Injectable, signal } from '@angular/core';
import { makeCoords, MapStatus, SeedData } from '../types';
import { MapStatusService } from './map-status.service';
import { Bounds, CRS, LatLngBounds, Layer, Point, TileLayer } from 'leaflet';
import * as L from 'leaflet';
import { HttpClient, HttpEvent } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { LayerService } from './layer.service';
import { StorageService } from '../../services/storage/storage.service';
import { PROJECT_PROVIDER, ProjectProvider } from '../../core';
import { cloneDeep } from 'lodash';
import { OfflineGridLayer } from '../layers/offline-grid-layer';
import { ProjectPointsService } from '../project/project-points.service';
import { GLOBAL_OSM_STORE } from './global-tile-seed.service';

export type SeedEventFn = (seedData: SeedData) => void;

export interface SeedableLayer extends Layer {
	wmsParams?: L.WMSParams;
	options: any;
}

@Injectable({
	providedIn: 'root'
})
export class OfflineLayerService {

	// seeding data and progress
	seedRequest = new EventEmitter<0|1|2>();
	seedLayerRequest = new EventEmitter<string>();
	seedAllCompleted = new EventEmitter<void>();
	seedCancelled = new EventEmitter<void>();
	seedRequests = new Set<string>();
	seedData = signal(new Map<string, SeedData>());
	layerSeedData = new Map<Layer, SeedData>();

	// Abbruch-Signal für laufende Downloads
	private cancelRequested = false;

	storage = inject(StorageService);
	pps = inject(ProjectPointsService);

	constructor(
		private http: HttpClient,
		private mapStatusService: MapStatusService,
		private ls: LayerService,
		private msg: ToastrService,
		@Inject(PROJECT_PROVIDER) private ps: ProjectProvider
	) {
		msg.toastrConfig.timeOut = 10000;
	}

	downloadFile(url: string, options: any): Observable<HttpEvent<any>> {
    return this.http.get(url, {
      reportProgress: true,
      observe: 'events',
      responseType: 'blob',
			params: options
    });
  }

	async hasImages(layerName: string): Promise<false|number> {
		const projectId = this.ps.project();
		if (!projectId) {
			return false;
		}
		const maxNativeZoom = await this.ps.getProjectValue('maxZoomLevel', layerName) as number | undefined;
		return maxNativeZoom ? maxNativeZoom : false;
	}

	/**
	 * Ermittelt die maximale heruntergeladene Zoomstufe über alle Layer hinweg.
	 * @returns Die höchste gespeicherte Zoomstufe oder null wenn keine Tiles heruntergeladen wurden.
	 */
	async getMaxDownloadedZoomLevel(): Promise<number | null> {
		const mapDef = this.ls.mapDef();
		if (!mapDef) return null;

		let maxZoom: number | null = null;

		for (const ld of mapDef.layers) {
			const layerMaxZoom = await this.hasImages(ld.name);
			if (layerMaxZoom !== false && (maxZoom === null || layerMaxZoom > maxZoom)) {
				maxZoom = layerMaxZoom;
			}
		}

		return maxZoom;
	}

	/**
	 * Generiert die Menge aller benötigten Tile-Keys für die gegebenen Regionen und Zoomlevels.
	 */
	getRequiredTileKeys(regions: LatLngBounds[], zoomLevels: number[], plusLevels = 0): Set<string> {
		zoomLevels = zoomLevels.sort((a, b) => a - b);
		const tileSet = new Set<string>();
		let remainingPlusLevels = plusLevels;

		for (const z of zoomLevels) {
			const tilesBeforeLevel = tileSet.size;
			for (const bounds of regions) {
				const tileBounds = this._tileBoundsForLatLngBounds(bounds, z);
				if (!tileBounds.min || !tileBounds.max) continue;
				for (let x = tileBounds.min.x; x <= tileBounds.max.x; x++) {
					for (let y = tileBounds.min.y; y <= tileBounds.max.y; y++) {
						tileSet.add(`${z}_${x}_${y}`);
					}
				}
			}
			const numberOfTilesForLevel = tileSet.size - tilesBeforeLevel;
			if (numberOfTilesForLevel > 1000 && !(remainingPlusLevels--)) {
				// Entferne die Tiles dieses Levels wieder
				for (const key of tileSet) {
					if (key.startsWith(`${z}_`)) tileSet.delete(key);
				}
				break;
			}
		}

		return tileSet;
	}

	/**
	 * Ermittelt den Download-Status für einen einzelnen Layer.
	 * @param layerName Name des Layers
	 * @param inputRegions Optional: Regions direkt übergeben statt aus Storage laden
	 * @param maxNativeZoom Optional: Maximale native Zoomstufe des Layers (z.B. 16 für FGK)
	 * @param minDownloadZoom Optional: Minimale Zoomstufe für Download (z.B. 12 für OSM mit MBTiles z7-z11)
	 */
	async getLayerDownloadStatus(layerName: string, inputRegions?: LatLngBounds[], maxNativeZoom?: number, minDownloadZoom?: number): Promise<'none' | 'partial' | 'complete'> {
		if (!this.ps.project()) return 'none';

		const projectId = this.ps.project();
		let regions: LatLngBounds[] = inputRegions ?? [];
		if (regions.length === 0) {
			const regionsData: any = await this.storage.getProjectValue('regions', projectId);
			regions = regionsData?.regions.map((r: any) => {
				// Migration: Alte Daten haben _southWest direkt, neue haben bounds._southWest
				const bounds = r.bounds ?? r;
				return new LatLngBounds(bounds._southWest, bounds._northEast);
			}) ?? [];
		}
		if (regions.length === 0) return 'none';

		// Zoom-Levels anpassen (z.B. FGK nur bis Zoom 16, OSM ab Zoom 12 wegen MBTiles)
		const minZoom = minDownloadZoom ?? 7;
		const maxZoom = maxNativeZoom ?? 18;
		const zoomLevels = [7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18].filter(z => z >= minZoom && z <= maxZoom);
		const plusLevels = Math.min(Math.max(parseInt(this.storage.getSync('offlinePlusLevels') || '0'), 0), 2) as 0|1|2;

		const requiredKeys = this.getRequiredTileKeys(regions, zoomLevels, plusLevels);
		const storedKeys = await this.ps.listKeys(layerName);
		const globalKeys = new Set(await this.storage.listKeys(GLOBAL_OSM_STORE) ?? []);

		const tileKeys = storedKeys?.filter(k => k !== 'maxZoomLevel') ?? [];
		if (tileKeys.length === 0 && globalKeys.size === 0) return 'none';

		const storedSet = new Set(tileKeys);
		let allPresent = true;
		for (const key of requiredKeys) {
			if (!storedSet.has(key) && !globalKeys.has(key)) {
				allPresent = false;
				break;
			}
		}

		const hasAny = tileKeys.length > 0 || [...requiredKeys].some(k => globalKeys.has(k));
		if (!hasAny) return 'none';
		return allPresent ? 'complete' : 'partial';
	}

	async isComplete(layerName: string, zoomLevels: number[], regions: LatLngBounds[]): Promise<boolean> {
		if (!this.ps.project()) return false;
		const plusLevels = Math.min(Math.max(parseInt(this.storage.getSync('offlinePlusLevels') || '0'), 0), 2) as 0|1|2;
		if (regions.length === 0) return false;

		const requiredKeys = this.getRequiredTileKeys(regions, zoomLevels, plusLevels);
		const storedKeys = await this.ps.listKeys(layerName);
		if (!storedKeys) return false;

		const storedSet = new Set(storedKeys);
		for (const key of requiredKeys) {
			if (!storedSet.has(key)) return false;
		}
		return true;
	}

	private readonly geodataTypes = new Set(['gpkg', 'geotiff', 'geojson']);
	// Layer-Typen die keinen Download-Status haben (dynamisch generiert)
	private readonly noDownloadStatusTypes = new Set(['grid']);

	/**
	 * Ermittelt den Download-Status für ein spezifisches Projekt (nur Tile-Layer).
	 * @param projectId Die ID des Projekts
	 * @param inputRegions Optional: Regions direkt übergeben statt aus Storage laden
	 * @param inputMapDef Optional: MapDefinition direkt übergeben statt aus Storage laden
	 * @returns 'none' wenn keine Tiles vorhanden, 'partial' wenn einige Tiles vorhanden, 'complete' wenn alle Tiles vorhanden
	 */
	async getDownloadStatusForProject(projectId: string, inputRegions?: LatLngBounds[], inputMapDef?: any): Promise<'none' | 'partial' | 'complete'> {
		if (!projectId) return 'none';

		// Lade Regions aus Projektspeicher falls nicht übergeben oder leer
		let regions: LatLngBounds[] = inputRegions && inputRegions.length > 0 ? inputRegions : [];
		if (regions.length === 0) {
			const regionsData: any = await this.storage.getProjectValue('regions', projectId);
			regions = regionsData?.regions?.map((r: any) => {
				// Migration: Alte Daten haben _southWest direkt, neue haben bounds._southWest
				const bounds = r.bounds ?? r;
				return new LatLngBounds(bounds._southWest, bounds._northEast);
			}) ?? [];
		}
		if (regions.length === 0) return 'none';

		// Verwende übergebene MapDefinition oder lade aus Projektspeicher
		const mapDef: any = inputMapDef ?? await this.storage.getProjectValue('mapDefinition', projectId);
		const layers = mapDef?.layers ?? [];
		if (layers.length === 0) return 'none';

		// Filtere Geodaten-Layer und dynamisch generierte Layer (z.B. Grid) aus
		const tileLayers = layers.filter((ld: any) => !this.geodataTypes.has(ld.type) && !this.noDownloadStatusTypes.has(ld.type));
		if (tileLayers.length === 0) return 'complete'; // Keine Tile-Layer vorhanden

		// plusLevels aus Projekt-Storage laden (dort wird es beim Download gespeichert)
		const storedPlusLevels = await this.storage.getProjectValue('offlinePlusLevels', projectId) as number | null;
		const plusLevels = Math.min(Math.max(storedPlusLevels ?? 0, 0), 2) as 0|1|2;

		// Sammle alle zu prüfenden Layer mit ihren Zoom-Bereichen
		// Nur sichtbare Layer (display !== false) berücksichtigen
		const layersToCheck: { name: string; maxNativeZoom: number; minDownloadZoom: number }[] = [];
		for (const ld of tileLayers) {
			// Überspringe ausgeblendete Layer
			if (ld.display === false) continue;

			// OSM-Layer: z7-z11 kommen aus MBTiles, Download erst ab z12
			const minDownloadZoom = ld.type === 'osm' ? 12 : 7;

			if (!ld.layers) {
				layersToCheck.push({ name: ld.name, maxNativeZoom: ld.maxNativeZoom ?? 18, minDownloadZoom });
			} else {
				for (const sublayer of ld.layers) {
					if (!sublayer.enabled) continue;
					layersToCheck.push({ name: sublayer.title, maxNativeZoom: ld.maxNativeZoom ?? 18, minDownloadZoom });
				}
			}
		}

		if (layersToCheck.length === 0) return 'complete';

		// Berechne requiredKeys pro Zoom-Bereich (Cache)
		// Dies spart erheblich CPU, da die meisten Layer denselben Zoom-Bereich haben
		const requiredKeysCache = new Map<string, Set<string>>();
		const getRequiredKeysForZoomRange = (minZoom: number, maxZoom: number): Set<string> => {
			const cacheKey = `${minZoom}-${maxZoom}`;
			if (requiredKeysCache.has(cacheKey)) {
				return requiredKeysCache.get(cacheKey)!;
			}
			const zoomLevels = [7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18].filter(z => z >= minZoom && z <= maxZoom);
			const keys = this.getRequiredTileKeys(regions, zoomLevels, plusLevels);
			requiredKeysCache.set(cacheKey, keys);
			return keys;
		};

		// Globale OSM-Tiles einbeziehen (einmal laden, für alle Layer nutzbar)
		const globalKeys = new Set(await this.storage.listKeys(GLOBAL_OSM_STORE) ?? []);

		// Alle Layer parallel prüfen (nur lokaler IndexedDB-Zugriff)
		const results = await Promise.all(layersToCheck.map(async (layer) => {
			const requiredKeys = getRequiredKeysForZoomRange(layer.minDownloadZoom, layer.maxNativeZoom);
			const storedKeys = await this.storage.listKeys(projectId, layer.name);

			const tileKeys = storedKeys?.filter(k => k !== 'maxZoomLevel') ?? [];
			const storedSet = new Set(tileKeys);
			const hasTiles = tileKeys.length > 0 || [...requiredKeys].some(k => globalKeys.has(k));
			let isComplete = true;
			for (const key of requiredKeys) {
				if (!storedSet.has(key) && !globalKeys.has(key)) {
					isComplete = false;
					break;
				}
			}
			return { hasTiles, complete: isComplete };
		}));

		const hasAnyTiles = results.some(r => r.hasTiles);
		const allComplete = results.every(r => r.complete);

		if (!hasAnyTiles) return 'none';
		if (allComplete) return 'complete';
		return 'partial';
	}

	/**
	 * Ermittelt den Status der Geodaten-Dateien für ein Projekt.
	 * @param projectId Die ID des Projekts
	 * @param inputMapDef Optional: MapDefinition direkt übergeben statt aus Storage laden
	 * @returns 'none' wenn keine Geodaten vorhanden, 'partial' wenn einige fehlen, 'complete' wenn alle vorhanden, 'not_applicable' wenn keine Geodaten im Projekt
	 */
	async getGeodataFilesStatus(projectId: string, inputMapDef?: any): Promise<'none' | 'partial' | 'complete' | 'not_applicable'> {
		if (!projectId) return 'not_applicable';

		const mapDef: any = inputMapDef ?? await this.storage.getProjectValue('mapDefinition', projectId);
		const layers = mapDef?.layers ?? [];

		// Finde alle Geodaten-Layer
		const geodataLayers = layers.filter((ld: any) => this.geodataTypes.has(ld.type));
		if (geodataLayers.length === 0) return 'not_applicable';

		// Alle Geodaten-Layer parallel prüfen (nur lokaler IndexedDB-Zugriff)
		const results = await Promise.all(geodataLayers.map(async (ld: any) => {
			const exists = await this.storage.getProjectValue(ld.name, projectId, 'attachments');
			return !!exists;
		}));

		const hasAny = results.some(r => r);
		const allPresent = results.every(r => r);

		if (!hasAny) return 'none';
		if (allPresent) return 'complete';
		return 'partial';
	}

	async storeLayerImages(l: L.Layer, layerName: string, regions: LatLngBounds[], zoomLevels: { min: number; max: number } | number[], plusLevels: number, startFn: SeedEventFn, progressFn: SeedEventFn, completeFn: SeedEventFn) {
		const wmsL = l as TileLayer.WMS;
		try {
			const maxZoomLevel = await this._preloadTiles(wmsL, regions, zoomLevels, plusLevels, layerName, startFn, progressFn);
			if (maxZoomLevel) {
				console.log(`Tiles bis Zoomlevel ${maxZoomLevel} gespeichert`);
				await this.ps.setProjectValue('maxZoomLevel', maxZoomLevel, layerName);
				const sd = this.seedData().get(layerName);
				if (sd) completeFn(sd);
			}
		} catch (e: any) {
			console.warn('Fehler beim Speichern der Tiles:', e);
		}
	}

	async seedAllLayers(plusLevels: 0|1|2, mapLayers: Record<string, Layer>, mapStatus: MapStatus | undefined, onAllCompleted?: () => void, singleLayer?: Layer) {
		// Cancel-Flag zurücksetzen bei neuem Start
		this.cancelRequested = false;

		const promises: Promise<void>[] = [];
		for (const layer of Object.values(mapLayers) as any[]) {
			// TileLayer (WMS) oder GridLayer (OSM/OfflineGridLayer) unterstützen
			if (!(layer instanceof L.TileLayer) && !(layer instanceof L.GridLayer)) continue;
			if (singleLayer && layer !== singleLayer) continue;
			const l = layer as SeedableLayer;
			const seedStartFn = (seedData: SeedData) => {
				this.layerSeedData.set(l, seedData);
				const asd = this.accumulatedSeedData();
				if (asd) {
					this.updateSeedData(m => m.set(asd.name, asd));
				}
			};
			const seedEndFn = (seedData: SeedData) => {
				if (this.layerSeedData.has(l)) {
					const data = this.layerSeedData.get(l) as SeedData;
					this.updateSeedData(m => m.delete(data.name));
					this.layerSeedData.delete(l);
				}
			};
			const seedProgressFn = (seedData: SeedData) => {
				if (this.layerSeedData.has(l)) {
					const data = this.layerSeedData.get(l) as SeedData;
					data.remainingLength = seedData.remainingLength;
					data.zoomLevel = seedData.zoomLevel;
					this.updateSeedData(m => m.set(data.name, data));
				}
			};
			// Nur aktive Bereiche für Download verwenden
			const allRegions = this.ls.regions()?.regions ?? [];
			const activeIndices = this.pps.activeRegionIndices();
			const activeRegions = activeIndices.length > 0
				? activeIndices.filter(i => i < allRegions.length).map(i => allRegions[i])
				: allRegions;  // Fallback: alle Bereiche wenn keine explizit aktiv
			const regionBounds = activeRegions.map(r => r.bounds);
			const regions = regionBounds.length > 0 ? regionBounds : (mapStatus?.bounds ? [mapStatus.bounds] : []);
			if (regions.length > 0) {
				// Name aus options.name lesen (wird in makeWMSTileLayers gesetzt)
				const name = (l as any).options?.name;
				if (!name) {
					console.warn('Layer hat keinen Namen, überspringe:', l);
					continue;
				}
				// maxNativeZoom aus Layer-Options lesen (z.B. für FGK auf 16 limitiert)
				const maxNativeZoom = (l as any).options?.maxNativeZoom;
				// Wenn MBTiles vorhanden, ab mbtilesMaxZoom+1 downloaden (z7-z11 kommen aus MBTiles)
				const mbtilesMaxZoom = (l as any).options?.mbtilesMaxZoom;
				const minDownloadZoom = mbtilesMaxZoom ? mbtilesMaxZoom + 1 : 7;
				const zoomLevels = { min: minDownloadZoom, max: maxNativeZoom ?? 18 };
				promises.push(this.storeLayerImages(l, name, regions, zoomLevels, plusLevels, seedStartFn, seedProgressFn, seedEndFn));
			}
		}
		await Promise.all(promises);

		// Bei Abbruch: seedData aufräumen und Event emittieren
		if (this.cancelRequested) {
			this.layerSeedData.clear();
			this.seedData.set(new Map());
			this.seedCancelled.emit();
		} else {
			onAllCompleted?.();
			this.seedAllCompleted.emit();
		}
	}

	accumulatedSeedData(): SeedData | undefined {
		let data: SeedData | undefined;
		for (const d of this.layerSeedData.values()) {
			if (!data) data = cloneDeep(d);
			else {
				if (data.queueLength && d.queueLength) data.queueLength += d.queueLength;
				else if (d.queueLength) data.queueLength = d.queueLength;
				if (data.remainingLength && d.remainingLength) data.remainingLength += d.remainingLength;
				else if (d.remainingLength) data.remainingLength = d.remainingLength;
				if (data.zoomLevel && d.zoomLevel) data.zoomLevel = Math.min(data.zoomLevel, d.zoomLevel);
				else if (d.zoomLevel) data.zoomLevel = d.zoomLevel;
			}
		}
		return data;
	}

	isSeeding(layerName?: string) {
		if (layerName)
			return this.seedData().has(layerName);
		return this.seedData().size > 0;
	}

	/**
	 * Bricht laufende Downloads ab
	 */
	cancelSeeding(): void {
		this.cancelRequested = true;
	}

	/**
	 * Prüft ob Abbruch angefordert wurde
	 */
	isCancelRequested(): boolean {
		return this.cancelRequested;
	}

	private updateSeedData(fn: (map: Map<string, SeedData>) => void) {
		const newMap = new Map(this.seedData());
		fn(newMap);
		this.seedData.set(newMap);
	}

	seedCurrentProgress(sd: SeedData) {
		if (!sd) return 0;
		return sd.queueLength - (sd.remainingLength ? sd.remainingLength : sd.queueLength);
	}

	seedMaxProgress(sd: SeedData) {
		if (!sd) return 0;
		return sd.queueLength;
	}

	/**
	 * Lädt alle WMS-Tiles für bestimmte Regionen + Zoomlevels und speichert sie in IndexedDB.
	 */
	private async _preloadTiles<LayerType extends L.TileLayer>(l: LayerType,
		regions: L.LatLngBounds[],
		zoomLevels: { min: number; max: number } | number[],
		plusLevels: number,
		layerName: string,
		startFn: SeedEventFn,
		progressFn: SeedEventFn
	): Promise<number|void> {
		const levels = Array.isArray(zoomLevels) ? zoomLevels.sort((a, b) => a - b)
			: Array.from({ length: zoomLevels.max - zoomLevels.min + 1 }, (_, i) => i + zoomLevels.min);

		const retries = 3;
		const concurrency = 16; // Erhöht für schnelleren Download
		const effectiveLevels: number[] = [];

		// Sammle alle einzigartigen Tile-Koordinaten über alle Regionen
		const tileSet = new Set<string>();

		for (const z of levels) {
			let totalTilesForLevel = 0;
			for (const bounds of regions) {
				const tileBounds = this._tileBoundsForLatLngBounds(bounds, z);
				if (!tileBounds.min || !tileBounds.max) continue;

				for (let x = tileBounds.min.x; x <= tileBounds.max.x; x++) {
					for (let y = tileBounds.min.y; y <= tileBounds.max.y; y++) {
						const key = `${z}_${x}_${y}`;
						if (!tileSet.has(key)) {
							tileSet.add(key);
							totalTilesForLevel++;
						}
					}
				}
			}

			if (totalTilesForLevel === 0) {
				this.msg.warning(`⚠️ Ungültige Tile-Grenzen für Zoomstufe ${z}. Überspringe diese Stufe.`);
				continue;
			}
			if (totalTilesForLevel > 1000 && !(plusLevels--)) {
				break;
			}
			effectiveLevels.push(z);
		}

		// Berechne die tatsächliche Anzahl der Tiles für die effektiven Levels
		const effectiveTileSet = new Set<string>();
		for (const z of effectiveLevels) {
			for (const bounds of regions) {
				const tileBounds = this._tileBoundsForLatLngBounds(bounds, z);
				if (tileBounds.min && tileBounds.max) {
					for (let x = tileBounds.min.x; x <= tileBounds.max.x; x++) {
						for (let y = tileBounds.min.y; y <= tileBounds.max.y; y++) {
							effectiveTileSet.add(`${z}_${x}_${y}`);
						}
					}
				}
			}
		}

		const effectiveNumberOfTiles = effectiveTileSet.size;

		// Kombinierte Bounds für SeedData
		const combinedBounds = regions.reduce((acc, b) => acc.extend(b), regions[0] ? new LatLngBounds(regions[0].getSouthWest(), regions[0].getNorthEast()) : new LatLngBounds([0,0], [0,0]));

		const sd: SeedData = {
			name: layerName,
			bbox: combinedBounds,
			minZoom: Math.min(...effectiveLevels),
			maxZoom: Math.max(...effectiveLevels),
			queueLength: effectiveNumberOfTiles,
			remainingLength: effectiveNumberOfTiles,
			size: 0
		};
		const tileTasks: (() => Promise<number>)[] = [];
		startFn(sd);

		// Bereits vorhandene Tiles per Batch ermitteln (statt Einzelabfragen)
		const storedKeys = new Set(await this.ps.listKeys(layerName) ?? []);
		// Tiles im globalen OSM-Store nicht nochmal herunterladen
		const globalKeys = new Set(await this.storage.listKeys(GLOBAL_OSM_STORE) ?? []);

		for (const tileKey of effectiveTileSet) {
			if (storedKeys.has(tileKey) || globalKeys.has(tileKey)) continue;

			const [z, x, y] = tileKey.split('_').map(Number);
			const coords: L.Coords = makeCoords(x, y, z);
			let tileUrl: string;
			const lasany = l as any;
			if (l instanceof TileLayer.WMS) {
				// Standard Leaflet TileLayer.WMS
				tileUrl = this._getWmsTileUrl(lasany._url, lasany.wmsParams, coords);
			}
			else if (l instanceof OfflineGridLayer && lasany.options?.wmsUrl) {
				// OfflineGridLayer mit WMS-URL (Online-Modus mit Cache)
				tileUrl = this._getWmsTileUrlFromOptions(lasany.options.wmsUrl, lasany.options.wmsLayers, lasany.options.wmsFormat || 'image/png', coords);
			}
			else {
				// URL aus Layer-Options oder Fallback auf offizielles OSM
				const opts = (l as any).options;
				let urlTemplate: string;
				if (opts?.lowZoomUrl && opts?.lowZoomMaxZoom && z <= opts.lowZoomMaxZoom) {
					urlTemplate = opts.lowZoomUrl;
				} else if (opts?.onlineUrl) {
					urlTemplate = opts.onlineUrl;
				} else {
					urlTemplate = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
				}
				tileUrl = urlTemplate.replace('{s}', 'a').replace('{z}', String(z)).replace('{x}', String(x)).replace('{y}', String(y));
			}

			tileTasks.push(async () => {
				return await this._fetchAndStoreTile(tileUrl, tileKey, layerName, retries);
			});
		}

		const total = tileTasks.length;
		let failedTiles = 0;

		// --- Batch-Verarbeitung mit Limit
		await this._runWithConcurrency(tileTasks, concurrency, (size: number) => {
			sd.remainingLength!--;
			if (size === 0) {
				failedTiles++;
			} else {
				sd.size! += size;
			}
			progressFn(sd);
		});
		sd.remainingLength = 0;
		progressFn(sd);

		// Warnung wenn Tiles fehlgeschlagen sind
		if (failedTiles > 0) {
			const successRate = Math.round(((total - failedTiles) / total) * 100);
			console.warn(`⚠️ ${layerName}: ${failedTiles} von ${total} Tiles fehlgeschlagen (${successRate}% erfolgreich)`);
			if (failedTiles > total * 0.1) {
				this.msg.warning(`⚠️ ${layerName}: ${failedTiles} Tiles konnten nicht heruntergeladen werden`);
			}
		}

		return Math.max(...effectiveLevels);
	}

	/**
	 * Lädt ein Tile mit Retries und speichert es in IndexedDB.
	 */
	private async _fetchAndStoreTile(
		url: string,
		key: string,
		layerName: string,
		retries: number
	): Promise<number> {
		let lastError: any;
		for (let attempt = 1; attempt <= retries; attempt++) {
			try {
				const response = await fetch(url);
				if (!response.ok) throw new Error(`HTTP ${response.status}`);
				const blob = await response.blob();
				const dataUrl = await this._blobToBase64(blob);
				const project = this.ps.project();
				if (!project) {
					console.error(`Kein Projekt beim Speichern von Tile ${layerName}/${key}!`);
				}
				await this.ps.setProjectValue(key, dataUrl, layerName);
				return blob.size;
			} catch (err) {
				lastError = err;
				if (attempt < retries) {
					console.warn(`Retry ${attempt}/${retries} für ${key}`);
					await this._sleep(300 * attempt); // exponentiell wachsender Delay
					// Weiter mit nächstem Versuch (kein return hier!)
				}
			}
		}
		// Alle Versuche fehlgeschlagen
		console.error(`❌ Tile fehlgeschlagen nach ${retries} Versuchen: ${key}`, lastError);
		return 0;
	}

	/**
	 * Führt Tasks mit begrenzter Parallelität aus.
	 * Bricht ab wenn cancelRequested gesetzt ist.
	 */
	private async _runWithConcurrency<T>(
		tasks: (() => Promise<T>)[],
		limit: number,
		onEach?: (result: T) => void
	): Promise<void> {
		const running = new Set<Promise<void>>();

		for (const task of tasks) {
			// Abbruch prüfen
			if (this.cancelRequested) {
				break;
			}

			const p = task().then((res) => {
				onEach?.(res);
				running.delete(p);
			}).catch(() => {
				running.delete(p);
			});
			running.add(p);

			if (running.size >= limit) {
				// Warte bis mindestens ein Task fertig ist
				await Promise.race(running);
			}
		}

		// Warte auf noch laufende Tasks (auch bei Abbruch)
		await Promise.all(running);
	}

	public async tileAmountEstimate(regions: LatLngBounds[], zoomLevels: number[], layerName?: string, plusLevels = 0) {
		zoomLevels = zoomLevels.sort((a, b) => a - b);
		const numberOfKeys = layerName ? await this.ps.numberOfKeys(layerName) : Promise.resolve(0);
		let maxZoomLevel = numberOfKeys && !!layerName ? ((await this.ps.getProjectValue('maxZoomLevel', layerName)) as number && 18) : 18;

		const tileSet = new Set<string>();
		for (const z of zoomLevels) {
			const tilesBeforeLevel = tileSet.size;
			for (const bounds of regions) {
				const tileBounds = this._tileBoundsForLatLngBounds(bounds, z);
				if (!tileBounds.min || !tileBounds.max) continue;
				for (let x = tileBounds.min.x; x <= tileBounds.max.x; x++) {
					for (let y = tileBounds.min.y; y <= tileBounds.max.y; y++) {
						tileSet.add(`${z}_${x}_${y}`);
					}
				}
			}
			const numberOfTilesForLevel = tileSet.size - tilesBeforeLevel;
			if ((numberOfTilesForLevel > 1000 && !(plusLevels--)) || z > maxZoomLevel) {
				// Entferne die Tiles dieses Levels wieder
				for (const key of tileSet) {
					if (key.startsWith(`${z}_`)) tileSet.delete(key);
				}
				maxZoomLevel = z - 1;
				break;
			}
		}

		const factor = layerName ? 1.0 : this.ls.numberOfLayers();
		return { count: factor * tileSet.size, sizeEstimate: factor * tileSet.size * 50 * 1024, maxZoomLevel: maxZoomLevel };
	}

	/**
	 * Berechnet die Tile-Grenzen (x/y) für ein LatLngBounds und Zoomlevel.
	 */
	private _tileBoundsForLatLngBounds(bounds: L.LatLngBounds, zoom: number): L.Bounds {
		const nwTile = CRS.EPSG3857.latLngToPoint(bounds.getNorthWest(), zoom).divideBy(256).floor();
		const seTile = CRS.EPSG3857.latLngToPoint(bounds.getSouthEast(), zoom).divideBy(256).floor();
		return new Bounds(nwTile, seTile);
	}

	/**
	 * Generiert die WMS-URL für ein Tile (aus TileLayer.WMS wmsParams).
	 */
	private _getWmsTileUrl(wmsUrl: string, params: Record<string, any>, coords: L.Coords): string {
		// BoundingBox berechnen
		const tileSize = 256;
		const nwPoint = new Point(coords.x * tileSize, coords.y * tileSize);
		const sePoint = new Point((coords.x + 1) * tileSize, (coords.y + 1) * tileSize);

		const nw = CRS.EPSG3857.pointToLatLng(nwPoint, coords.z);
		const se = CRS.EPSG3857.pointToLatLng(sePoint, coords.z);

		const bbox = [nw.lng, se.lat, se.lng, nw.lat].join(',');

		const query = new URLSearchParams({
			service: 'WMS',
			request: 'GetMap',
			version: '1.1.1',
			srs: 'EPSG:4326',
			width: tileSize.toString(),
			height: tileSize.toString(),
			bbox,
			...params
		});

		return `${wmsUrl}?${query.toString()}`;
	}

	/**
	 * Generiert die WMS-URL für ein Tile (aus OfflineGridLayer options).
	 */
	private _getWmsTileUrlFromOptions(wmsUrl: string, layers: string, format: string, coords: L.Coords): string {
		const tileSize = 256;
		const nwPoint = new Point(coords.x * tileSize, coords.y * tileSize);
		const sePoint = new Point((coords.x + 1) * tileSize, (coords.y + 1) * tileSize);

		const nw = CRS.EPSG3857.pointToLatLng(nwPoint, coords.z);
		const se = CRS.EPSG3857.pointToLatLng(sePoint, coords.z);

		const bbox = [nw.lng, se.lat, se.lng, nw.lat].join(',');

		const query = new URLSearchParams({
			service: 'WMS',
			request: 'GetMap',
			version: '1.1.1',
			layers: layers,
			styles: '',
			format: format,
			transparent: 'true',
			srs: 'EPSG:4326',
			width: tileSize.toString(),
			height: tileSize.toString(),
			bbox
		});

		const separator = wmsUrl.includes('?') ? '&' : '?';
		return `${wmsUrl}${separator}${query.toString()}`;
	}

	/**
	 * Blob → Base64-DataURL
	 */
	private _blobToBase64(blob: Blob): Promise<string> {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onloadend = () => resolve(reader.result as string);
			reader.onerror = reject;
			reader.readAsDataURL(blob);
		});
	}

	/**
	 * Sleep helper
	 */
	_sleep(ms: number): Promise<void> {
		return new Promise((resolve) => setTimeout(resolve, ms));
	}

}
